/**
 * WebSocket wrapper with connection timeout + exponential-backoff reconnect.
 *
 * Fixes two client-side stability gaps:
 *   1. `new WebSocket(url)` can hang indefinitely when the server is unreachable.
 *      We force-close after OPEN_TIMEOUT_MS so a down server doesn't produce a
 *      silent, never-firing socket.
 *   2. A dropped connection should reconnect with backoff instead of spamming
 *      the server every 3s forever.
 *
 * Returned object exposes `.socket` (current WebSocket), `.send()` (guarded),
 * `.close()` (permanent disconnect — stops reconnect), and `.getState()`.
 * Users wire their own event handlers via `options.on*` callbacks.
 */

const OPEN_TIMEOUT_MS = 10_000;
const BACKOFF_STEPS_MS = [5_000, 10_000, 30_000, 60_000, 120_000];

export function createWsWithReconnect(url, options = {}) {
  const {
    onOpen,
    onMessage,
    onError,
    onClose,
    onReconnecting, // (attempt, delayMs) — UI hook for "connecting..." indicator
    shouldReconnect = () => true,
  } = options;

  let currentSocket = null;
  let reconnectTimer = null;
  let openTimer = null;
  let attempt = 0;
  let closedByUser = false;

  const cleanupTimers = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (openTimer) {
      clearTimeout(openTimer);
      openTimer = null;
    }
  };

  const connect = () => {
    if (closedByUser) return;

    const socket = new WebSocket(url);
    currentSocket = socket;

    openTimer = setTimeout(() => {
      // Server never completed the handshake — force-close so onclose fires
      // and we can schedule the next retry.
      if (socket.readyState !== WebSocket.OPEN) {
        try {
          socket.close(4000, "connect_timeout");
        } catch {}
      }
    }, OPEN_TIMEOUT_MS);

    socket.onopen = (ev) => {
      if (openTimer) {
        clearTimeout(openTimer);
        openTimer = null;
      }
      attempt = 0;
      onOpen?.(ev, socket);
    };

    socket.onmessage = (ev) => onMessage?.(ev, socket);

    socket.onerror = (ev) => onError?.(ev, socket);

    socket.onclose = (ev) => {
      if (openTimer) {
        clearTimeout(openTimer);
        openTimer = null;
      }
      onClose?.(ev, socket);
      if (closedByUser) return;
      if (!shouldReconnect(ev)) return;

      const delay = BACKOFF_STEPS_MS[Math.min(attempt, BACKOFF_STEPS_MS.length - 1)];
      attempt += 1;
      onReconnecting?.(attempt, delay);
      reconnectTimer = setTimeout(connect, delay);
    };
  };

  connect();

  return {
    get socket() {
      return currentSocket;
    },
    send(payload) {
      if (currentSocket?.readyState === WebSocket.OPEN) {
        try {
          currentSocket.send(payload);
          return true;
        } catch {
          return false;
        }
      }
      return false;
    },
    close(code = 1000, reason = "client_close") {
      closedByUser = true;
      cleanupTimers();
      if (currentSocket) {
        try {
          currentSocket.close(code, reason);
        } catch {}
        currentSocket = null;
      }
    },
    getState() {
      return currentSocket?.readyState ?? WebSocket.CLOSED;
    },
  };
}

export default createWsWithReconnect;
